import React, { useCallback, useEffect, useState } from 'react';
import { ArrowClockwise, Warning, DownloadSimple, Table as TableIcon } from '@phosphor-icons/react';
import { dmsApi } from './dmsApi';

const colLetter = (n) => {
  let s = '';
  let v = n;
  while (v > 0) { const m = (v - 1) % 26; s = String.fromCharCode(65 + m) + s; v = Math.floor((v - 1) / 26); }
  return s;
};

const borderCss = (bd) => {
  if (!bd) return {};
  const line = '1px solid #cbd5e1';
  return {
    borderTop: bd.includes('t') ? line : undefined,
    borderRight: bd.includes('r') ? line : undefined,
    borderBottom: bd.includes('b') ? line : undefined,
    borderLeft: bd.includes('l') ? line : undefined,
  };
};

/** Excel: セル結合・列幅・塗り・太字・配置を反映した表を組む */
function SheetView({ data, onSheet, onShowMore, busy }) {
  const widths = data.widths || [];
  const sheets = data.sheets || [];
  return (
    <div className="flex flex-col h-full min-h-0">
      {sheets.length > 0 && (
        <div className="shrink-0 flex items-center gap-1 px-2 py-1.5 border-b border-zinc-200 bg-zinc-50 overflow-x-auto"
          data-testid="dms-render-sheet-tabs">
          <span className="shrink-0 text-[10px] font-bold text-zinc-400 pr-1">シート</span>
          {sheets.map((s) => (
            <button key={s} type="button" onClick={() => onSheet(s)} disabled={busy}
              data-testid={`dms-render-sheet-tab-${s}`}
              className={`shrink-0 px-2.5 py-1 rounded text-[11px] font-medium transition-colors disabled:opacity-60 ${
                s === data.active ? 'bg-white border border-zinc-300 text-teal-700 shadow-sm'
                  : 'text-zinc-500 hover:text-zinc-800 hover:bg-white/70'}`}>
              {s}
            </button>
          ))}
          {busy && <ArrowClockwise size={12} className="shrink-0 ml-1 animate-spin text-zinc-400" />}
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-auto bg-white">
        <table className="border-collapse" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th className="sticky top-0 left-0 z-20 bg-zinc-100 border border-zinc-300 text-[10px] text-zinc-400 font-normal w-10" />
              {widths.map((w, i) => (
                <th key={i} style={{ width: w, minWidth: w }}
                  className="sticky top-0 z-10 bg-zinc-100 border border-zinc-300 text-[10px] text-zinc-500 font-normal px-1">
                  {colLetter(i + 1)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.rows || []).map((row) => (
              <tr key={row.r}>
                <td className="sticky left-0 z-10 bg-zinc-100 border border-zinc-300 text-[10px] text-zinc-500 text-center px-1 select-none">
                  {row.r}
                </td>
                {row.cells.length === 0 && widths.map((w, i) => (
                  <td key={i} className="border border-zinc-200" style={{ height: 22 }} />
                ))}
                {row.cells.length > 0 && (() => {
                  const byCol = new Map(row.cells.map((c) => [c.c, c]));
                  const out = [];
                  for (let ci = 1; ci <= widths.length; ci += 1) {
                    const c = byCol.get(ci);
                    if (!c) { out.push(<td key={ci} className="border border-zinc-200" style={{ height: 22 }} />); continue; }
                    out.push(
                      <td key={ci} rowSpan={c.rs || 1} colSpan={c.cs || 1}
                        className="border border-zinc-200 px-1.5 text-[11px] align-middle"
                        style={{
                          background: c.bg || undefined,
                          color: c.fc || undefined,
                          fontWeight: c.b ? 700 : undefined,
                          fontStyle: c.i ? 'italic' : undefined,
                          fontSize: c.fs ? `${c.fs}px` : undefined,
                          textAlign: c.a || undefined,
                          whiteSpace: c.w ? 'pre-wrap' : 'nowrap',
                          ...borderCss(c.bd),
                        }}>
                        {c.v || ''}
                      </td>,
                    );
                    if (c.cs && c.cs > 1) ci += c.cs - 1;
                  }
                  return out;
                })()}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.truncated && (
        <div className="shrink-0 flex items-center justify-center gap-2 px-3 py-1.5 border-t border-amber-200 bg-amber-50 text-[11px] text-amber-800">
          <span>
            一部のみ表示中（{data.shown_rows}/{data.total_rows}行 · {data.shown_cols}/{data.total_cols}列）
          </span>
          <button type="button" onClick={onShowMore} disabled={busy}
            data-testid="dms-render-show-all"
            className="px-2 py-0.5 rounded bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-50">
            {busy ? '読み込み中…' : '全体を表示（時間がかかります）'}
          </button>
        </div>
      )}
    </div>
  );
}

/** Word: 見出し・段落・表を本文の順番どおりに */
function DocView({ data }) {
  return (
    <div className="h-full overflow-auto bg-white px-6 py-5" data-testid="dms-render-doc">
      <div className="max-w-3xl mx-auto space-y-2.5">
        {(data.blocks || []).map((b, i) => {
          if (b.t === 'h') {
            const size = b.level <= 1 ? 'text-xl' : b.level === 2 ? 'text-base' : 'text-sm';
            return <p key={i} className={`${size} font-bold text-zinc-900 pt-3`}>{b.v}</p>;
          }
          if (b.t === 'table') {
            return (
              <table key={i} className="w-full border-collapse my-3 text-[11px]">
                <tbody>
                  {b.rows.map((r, ri) => (
                    <tr key={ri}>
                      {r.map((c, ci) => (
                        <td key={ci} className={`border border-zinc-300 px-2 py-1 align-top ${ri === 0 ? 'bg-zinc-50 font-semibold' : ''}`}>{c}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          }
          return (
            <p key={i} className={`text-[13px] leading-relaxed text-zinc-700 ${b.list ? 'pl-5 relative before:content-["・"] before:absolute before:left-1' : ''}`}>
              {b.v}
            </p>
          );
        })}
      </div>
    </div>
  );
}

/** PowerPoint: 1スライド1枚のカード */
function SlidesView({ data }) {
  return (
    <div className="h-full overflow-auto bg-zinc-100 px-4 py-4" data-testid="dms-render-slides">
      <div className="max-w-3xl mx-auto space-y-3">
        {(data.slides || []).map((s) => (
          <div key={s.no} className="bg-white rounded-lg border border-zinc-200 shadow-sm overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-zinc-50 border-b border-zinc-200">
              <span className="text-[10px] font-bold text-zinc-400">SLIDE {s.no}</span>
              {s.title && <span className="text-xs font-semibold text-zinc-800 truncate">{s.title}</span>}
            </div>
            <div className="px-4 py-3 space-y-2">
              {s.bodies.map((t, i) => (
                <p key={i} className="text-[12px] leading-relaxed text-zinc-700 whitespace-pre-wrap">{t}</p>
              ))}
              {s.tables.map((rows, ti) => (
                <table key={ti} className="w-full border-collapse text-[11px]">
                  <tbody>
                    {rows.map((r, ri) => (
                      <tr key={ri}>
                        {r.map((c, ci) => (
                          <td key={ci} className={`border border-zinc-300 px-2 py-1 ${ri === 0 ? 'bg-zinc-50 font-semibold' : ''}`}>{c}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ))}
              {!s.bodies.length && !s.tables.length && (
                <p className="text-[11px] text-zinc-400">（文字のないスライド）</p>
              )}
              {s.notes && (
                <p className="text-[11px] text-zinc-500 border-t border-dashed border-zinc-200 pt-2 mt-2 whitespace-pre-wrap">
                  ノート: {s.notes}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * LibreOffice が無くても中身を見せる描画。
 * サーバー側で openpyxl / python-docx / python-pptx で読み、ここで組み立てる。
 */
export function OfficeRender({ docId, doc, dark = false, heightClass = 'h-full' }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const muted = dark ? 'text-zinc-500' : 'text-zinc-400';

  const load = useCallback(async (opts = {}) => {
    setBusy(true);
    try {
      const d = await dmsApi.officeRender(docId, opts);
      setData(d);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || '表示できませんでした');
    } finally { setBusy(false); }
  }, [docId]);

  useEffect(() => { setData(null); setErr(null); load(); }, [docId, load]);

  if (err) {
    return (
      <div className={`py-12 px-6 text-center ${dark ? 'text-zinc-300' : 'text-zinc-600'}`}
        data-testid="dms-render-error">
        <Warning size={32} weight="duotone" className="mx-auto mb-2 text-amber-500" />
        <p className="text-xs font-semibold">{doc?.current_filename}</p>
        <p className={`text-[11px] mt-1 ${muted}`}>{err}</p>
        <button type="button" onClick={() => dmsApi.downloadFile(docId, null, doc?.current_filename)}
          className={`mt-3 px-3 py-1.5 rounded text-[11px] font-semibold border ${dark ? 'border-zinc-600 text-zinc-300' : 'border-zinc-300 text-zinc-600'}`}
          data-testid="dms-render-dl">
          <DownloadSimple size={12} className="inline mr-1" />ダウンロード
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={`py-12 text-center ${muted}`} data-testid="dms-render-loading">
        <ArrowClockwise size={30} className="mx-auto mb-2 animate-spin" />
        <p className="text-[11px]">中身を読み込んでいます…</p>
      </div>
    );
  }

  return (
    <div className={`w-full ${heightClass} flex flex-col min-h-0 rounded overflow-hidden ${dark ? 'border border-zinc-700' : 'border border-zinc-200'}`}
      data-testid="dms-office-render">
      <div className="flex-1 min-h-0">
        {data.kind === 'sheet' && (
          <SheetView data={data} busy={busy}
            onSheet={(s) => load({ sheet: s })}
            onShowMore={() => load({ sheet: data.active, maxRows: data.limits?.max_rows, maxCols: data.limits?.max_cols })} />
        )}
        {data.kind === 'doc' && <DocView data={data} />}
        {data.kind === 'slides' && <SlidesView data={data} />}
      </div>
      <p className={`shrink-0 text-[10px] ${muted} text-center py-1 ${dark ? 'bg-zinc-800/60' : 'bg-zinc-50'}`}>
        <TableIcon size={10} className="inline mr-1" />
        {data.kind === 'sheet'
          ? `${doc?.current_filename} を表形式で表示しています（${(data.sheets || []).length}シート・列は切れません）`
          : `${doc?.current_filename} の中身を表示しています（書式は簡略化されています）`}
      </p>
    </div>
  );
}

export default OfficeRender;
